import { asFunction } from "../../core/helpers/functional-util.js";
import KeyFigureValue from "./value/KeyFigureValue.js";
import Model from "../Model.js";

/**
 * `KeyFigureResult` contains the result from the query of values
 * - for a set of keyFigures
 * - in a given period
 * - aggregated by a period type
 * - with or without values for each the key figure component.
 *
 * @param {KeyFigure[]} [keyFigures] - List of keyFigures involved in the computation of the result
 * @param {KeyFigureComponentValue[]} [values] - List of values for each keyFigure
 * @param {Period[]} [periods] - List of periods included in the result
 */
export default class KeyFigureResult extends Model {
	constructor({ keyFigures = [], values = [], periods = [] } = {}) {
		super(arguments);

		this._keyFigures = keyFigures;
		this._values = values;
		this._periods = periods;
	}

	getPeriods() {
		return this._periods;
	}

	getKeyFigures() {
		return this._keyFigures;
	}

	getValues() {
		return this._values;
	}

	getFirstKeyFigure() {
		return this.getKeyFigures()[0];
	}

	getKeyFigureById(keyFigureId) {
		return this.getKeyFigures().find(
			(keyFigure) => keyFigure.getId() === keyFigureId,
		);
	}

	getValuesForKeyFigure(keyFigure) {
		return this.getValuesForKeyFigures([keyFigure]);
	}

	getValuesForKeyFigures(keyFigures) {
		const allowedIds = new Set(
			keyFigures.filter(Boolean).map((keyFigure) => keyFigure.getId()),
		);

		return this._values.filter((value) => {
			const component = value.getComponent?.();
			const componentId = component?.getId?.();
			return componentId && allowedIds.has(componentId);
		});
	}

	getValueForComponentInPeriod(component, period) {
		return this.getValuesForKeyFigure(component).find((value) =>
			value.getPeriod().equals(period),
		);
	}

	getValuesByKeyFigure() {
		return this._keyFigures.map((keyFigure) =>
			this.getValuesForKeyFigure(keyFigure),
		);
	}

	getMin() {
		return this._values.reduce(
			(val, current) =>
				val.getValue() && val.getValue() < current.getValue()
					? val
					: current,
			{ getValue: asFunction(Infinity) },
		);
	}

	getMax() {
		return this._values.reduce(
			(val, current) =>
				val.getValue() && val.getValue() > current.getValue()
					? val
					: current,
			{ getValue: asFunction(-Infinity) },
		);
	}

	/**
	 * Group all values into a map where keys are periods. One and
	 * only one value is associated with each period: this means that
	 * this method should not be used if the receiver has several
	 * values associated to the same period as the result would be
	 * unpredictable.
	 *
	 * @return {Map<Period, KeyFigureComponentValue>}
	 */
	asPeriodMap() {
		const result = new Map();

		for (const value of this.getValues()) {
			result.set(value.getPeriod(), value);
		}

		return result;
	}

	isEmpty() {
		return !this._values.length;
	}

	static transformMaterializationData(data, context) {
		if (data.periodIds) {
			data.periods = data.periodIds.map((id) =>
				context.findPeriodById(id),
			);
		}

		const keyFigures = data.keyFigures ? [...data.keyFigures] : [];
		context.keyFigures ||= {};

		for (const keyFigure of keyFigures) {
			if (keyFigure) context.keyFigures[keyFigure.getId()] = keyFigure;
		}

		const includedIds = new Set(
			keyFigures.map((keyFigure) => keyFigure.getId()),
		);
		for (const id of data.keyFigureIds || []) {
			if (!includedIds.has(id) && context.keyFigures[id]) {
				keyFigures.push(context.keyFigures[id]);
				includedIds.add(id);
			}
		}

		data.keyFigures = keyFigures;

		data.values = this._buildComponentValues(
			data.values,
			data.keyFigures,
			context,
		);
	}

	static _buildComponentValues(data, keyFigures, context) {
		const values = KeyFigureValue.materializeAll(data, context);
		const allowedIds = new Set(
			(keyFigures || [])
				.filter(Boolean)
				.map((keyFigure) => keyFigure.getId()),
		);

		return values.filter((value) => {
			const component = value.getComponent?.();
			const componentId = component?.getId?.();
			return componentId && allowedIds.has(componentId);
		});
	}
}
